/**
 * DataCache - A simple in-memory cache with TTL for API responses
 * Prevents unnecessary API calls when switching between pages
 */

import { clearAllDateRangesFromStorage } from "./DatePersistenceUtils";

class DataCache {
    constructor() {
        this.cache = new Map();
        this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
    }

    /**
     * Set data in cache with optional TTL
     * @param {string} key - Cache key
     * @param {any} data - Data to cache
     * @param {number} ttl - Time to live in milliseconds (optional)
     */
    set(key, data, ttl = this.defaultTTL) {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }

    /**
     * Get data from cache if not expired
     * @param {string} key - Cache key
     * @returns {any|null} - Cached data or null if not found/expired
     */
    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        if (Date.now() - item.timestamp > item.ttl) {
            this.cache.delete(key);
            return null;
        }

        return item.data;
    }

    /**
     * Check if key exists and is not expired
     * @param {string} key - Cache key
     * @returns {boolean} - True if key exists and is valid
     */
    has(key) {
        const item = this.cache.get(key);
        if (!item) return false;

        if (Date.now() - item.timestamp > item.ttl) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    /**
     * Delete specific key from cache
     * @param {string} key - Cache key to delete
     */
    delete(key) {
        this.cache.delete(key);
    }

    /**
     * Clear all cached data
     */
    clear() {
        this.cache.clear();
    }

    /**
     * Get cache statistics
     * @returns {object} - Cache stats
     */
    getStats() {
        const now = Date.now();
        let validEntries = 0;
        let expiredEntries = 0;

        for (const [key, item] of this.cache.entries()) {
            if (now - item.timestamp > item.ttl) {
                expiredEntries++;
                this.cache.delete(key);
            } else {
                validEntries++;
            }
        }

        return {
            totalEntries: validEntries + expiredEntries,
            validEntries,
            expiredEntries,
            cacheSize: this.cache.size,
        };
    }

    /**
     * Clean up expired entries
     */
    cleanup() {
        const now = Date.now();
        for (const [key, item] of this.cache.entries()) {
            if (now - item.timestamp > item.ttl) {
                this.cache.delete(key);
            }
        }
    }
}

// Create singleton instance
export const dataCache = new DataCache();

// Auto-cleanup every 10 minutes
setInterval(() => {
    dataCache.cleanup();
    clearAllDateRangesFromStorage();
}, 10 * 60 * 1000);

/**
 * Hook for using cached data with automatic cache management
 * @param {string} cacheKey - Unique key for the data
 * @param {Function} fetchFn - Function to fetch data if not cached
 * @param {number} ttl - Time to live in milliseconds (optional)
 * @returns {Promise<any>} - Cached or fresh data
 */
export const useCachedData = async (cacheKey, fetchFn, ttl) => {
    // Check cache first
    const cachedData = dataCache.get(cacheKey);
    if (cachedData) {
        return cachedData;
    }

    // Fetch fresh data
    const freshData = await fetchFn();

    // Cache the result
    dataCache.set(cacheKey, freshData, ttl);

    return freshData;
};

/**
 * Utility to generate cache keys for API requests
 * @param {string} endpoint - API endpoint
 * @param {object} params - Request parameters
 * @returns {string} - Generated cache key
 */
export const generateCacheKey = (endpoint, params = {}) => {
    const paramString = Object.keys(params)
        .sort()
        .map((key) => `${key}:${params[key]}`)
        .join("|");

    return `${endpoint}${paramString ? `|${paramString}` : ""}`;
};

export default dataCache;
